import React, { useEffect, useState } from 'react';
import { Bell, Loader2, FileText, Image as ImageIcon } from 'lucide-react';
import { novedadesApi } from '../../lib/api';

const fmtFecha = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

// Torre Emilia primero, Foresta después, resto alfabético.
const ordenEdificio = (a, b) => {
  const rank = (n) => {
    const x = (n || '').toLowerCase();
    if (x.includes('emilia')) return 0;
    if (x.includes('foresta')) return 1;
    return 2;
  };
  const ra = rank(a), rb = rank(b);
  return ra !== rb ? ra - rb : a.localeCompare(b);
};

export default function PropietarioNovedades() {
  const [novedades, setNovedades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    novedadesApi.portal()
      .then((r) => setNovedades(r.data || []))
      .catch(() => setNovedades([]))
      .finally(() => setLoading(false));
  }, []);

  const porEdificio = {};
  for (const n of novedades) {
    const key = n.edificio_nombre || 'Otros';
    (porEdificio[key] = porEdificio[key] || []).push(n);
  }
  const edificios = Object.keys(porEdificio).sort(ordenEdificio);

  return (
    <div className="space-y-6 max-w-3xl mx-auto px-3 sm:px-0 py-2" data-testid="propietario-novedades">
      <div className="flex items-center gap-3">
        <Bell className="w-7 h-7 text-[#1B4D3E]" strokeWidth={1.5} />
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Novedades</h1>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : novedades.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1} />
          <p className="text-slate-500 text-lg">No hay novedades por el momento</p>
          <p className="text-slate-400 text-sm mt-1">Las novedades de tus unidades aparecerán aquí</p>
        </div>
      ) : (
        edificios.map((edificioNombre) => (
          <div key={edificioNombre} data-testid={`novedades-edificio-${edificioNombre}`}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{edificioNombre}</h3>
              <span className="text-xs text-slate-400">({porEdificio[edificioNombre].length})</span>
            </div>
            <div className="space-y-3">
              {porEdificio[edificioNombre].map((n) => (
                <div key={n.novedad_id} data-testid={`novedad-${n.novedad_id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-slate-900">{n.titulo}</h4>
                    <span className="text-xs text-slate-400 flex-shrink-0">{fmtFecha(n.published_at || n.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{n.texto}</p>
                  {(n.adjuntos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {n.adjuntos.map((a) => (
                        <a key={a.file_id} href={novedadesApi.downloadUrl(a.file_id)} target="_blank" rel="noreferrer"
                          data-testid={`adjunto-${a.file_id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#1B4D3E] bg-emerald-50 border border-emerald-100 rounded px-2 py-1 hover:bg-emerald-100">
                          {a.content_type?.includes('pdf') ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                          <span className="max-w-[160px] truncate">{a.original_filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
